import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import {
	MatLegacyDialogRef as MatDialogRef,
	MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA
} from "@angular/material/legacy-dialog";
import { map, take, takeUntil } from "rxjs/operators";
import { Observable, Subject } from "rxjs";
import { Store } from "@ngrx/store";
import { ApiService } from "@modules/pay-groups/services/api.service";
import { LegalEntityService } from "@shared/services/legal-entity/legal-entity.service";
import { PayGroup, updateEmployeePayGroupDTO } from "@shared/models/pay-groups";
import { SelectOption } from "@shared/models/select-option.interface";
import { Employee } from "src/app/shared/models/employee.interface";
import { BreadcrumbService } from "@shared/components/breadcrumbs/services/breadcrumb.service";
import { AppState } from "@store/models/state.model";
import { ToastService } from "@shared/services/toast/toast.service";
import { FeatureFlagCodes } from "@shared/constants/feature-flag-codes";
import { FeatureManagementService } from "@shared/services/feature-management/feature-management.service";

@Component({
	selector: "app-entity-paygroup-select-dialog",
	templateUrl: "./entity-paygroup-select-dialog.component.html",
	styleUrls: ["./entity-paygroup-select-dialog.component.scss"]
})
export class EntityPaygroupSelectDialogComponent implements OnInit, OnDestroy {
	destroy$: Subject<void> = new Subject();
	entities$: Observable<SelectOption[]> = new Observable<SelectOption[]>();
	payGroups$: Observable<SelectOption[]> = new Observable<SelectOption[]>();
	payGroupOptions: SelectOption[] = [];
	employeeVersion!: number;
	form!: FormGroup;
	currentCustomerLegalEntities: string[] = [];
	cpNowIntegrationEnabled: boolean = false;
	employee!: Employee;

	constructor(
		@Inject(MAT_DIALOG_DATA)
		public data: { customerId: string; legalEntityId: string; payGroup: PayGroup; employeeId: string },
		public dialogRef: MatDialogRef<EntityPaygroupSelectDialogComponent>,
		private legalEntityService: LegalEntityService,
		private payGroupService: ApiService,
		private formBuilder: FormBuilder,
		private breadCrumbService: BreadcrumbService,
		private store: Store<AppState>,
		private toastService: ToastService,
		private featureManagementService: FeatureManagementService
	) {}

	ngOnInit(): void {
		this.getEntities();
		this.initForm();
		this.initSubscriptions();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	initForm(): void {
		this.form = this.formBuilder.group({
			payGroup: [this.data.payGroup.id]
		});
	}

	initSubscriptions(): void {
		this.checkCPNowIntegrationFeature();
		this.getEmployeeVersion(this.data.employeeId);
	}

	onCancel(): void {
		this.dialogRef.close(false);
	}

	getEmployeeVersion(employeeId: string) {
		this.payGroupService
			.getEmployee(employeeId)
			.pipe(
				take(1),
				map((employee: Employee) => {
					this.employee = employee;
				})
			)
			.subscribe();
	}

	getEntities(): void {
		this.entities$ = this.legalEntityService.getLegalEntitiesForCustomerAsSelectOption(this.data.customerId);

		this.entities$.pipe(take(1)).subscribe({
			next: res => {
				res.forEach(element => {
					this.currentCustomerLegalEntities.push(element.value);
				});
				this.getPayGroups(this.currentCustomerLegalEntities);
			}
		});
	}

	getPayGroups(legalEntityIds: string[]): void {
		const statuses: string = "LIVE, IMPLEMENTATION";
		this.payGroupService
			.getPayGroupsByLegalEntityIdAsSelectOptions([], statuses, this.data.customerId)
			.pipe(
				takeUntil(this.destroy$),
				map(groups => {
					this.payGroupOptions = groups.filter(group => group.value !== this.data.payGroup.id);
				})
			)
			.subscribe();
	}

	onConfirm(): void {
		this.dialogRef.close(this.form.getRawValue());

		let updateEmployeePayGroupDTO: updateEmployeePayGroupDTO = {
			version: this.employee?.version!,
			payGroupId: this.form.get("payGroup")?.value,
			employeeId: this.data.employeeId
		};
		let updateEmployeeObservable!: Observable<Employee>;

		if (this.cpNowIntegrationEnabled) {
			updateEmployeeObservable = this.payGroupService.updateEmployeePayGroupV2(updateEmployeePayGroupDTO);
		} else {
			updateEmployeeObservable = this.payGroupService.updateEmployeePayGroup(updateEmployeePayGroupDTO);
		}

		updateEmployeeObservable.pipe(take(1)).subscribe({
			next: () => {
				// update customer entity group to
				this.breadCrumbService.updateCustomerEntityGroupStatePayGroup(this.form.get("payGroup")?.value);
				this.toastService.showSuccess("Employee updated successfully");

				location.reload();
			},
			error: error => {
				console.log("Error during PATCH request");
				console.error(error);
			}
		});
	}

	private checkCPNowIntegrationFeature(): void {
		this.featureManagementService
			.hasFeature(FeatureFlagCodes.Q1_2026_WPAY_CPNOW_INTEGRATION)
			.pipe(take(1))
			.subscribe(hasFeature => {
				this.cpNowIntegrationEnabled = hasFeature;
			});
	}
}
